<script setup lang="ts">
/**
 * CaLightbox — pratinjau gambar layar penuh, tanpa library eksternal.
 *
 * Kunci desain: membungkus <dialog> NATIVE (Teleport ke body). Dari browser
 * kita dapat GRATIS: focus-trap, tombol ESC, penonaktifan latar (inert), dan
 * ::backdrop. Didukung Chrome Android modern — peramban mayoritas pengguna.
 *
 * Semua gerak HANYA lewat transform + opacity (GPU, nol properti layout yang
 * dianimasikan). prefers-reduced-motion mematikan seluruh transisi.
 *
 * Interaksi memakai Pointer Events (satu jalur untuk sentuh + tetikus):
 *   · zoom  : tombol +/−, roda tetikus, cubit 2-jari, ketuk-ganda (1x↔2.5x)
 *   · geser : seret 1-jari saat skala > 1 (pan)
 *   · putar : tombol 90° (0/90/180/270)
 *   · tutup : ESC (native), ketuk latar, tombol ×
 *
 * API:
 *   <CaLightbox v-model:open="buka" :src="src" :nama="nama" />
 * Anggaran: komponen tunggal, nol dependensi.
 */
const props = withDefaults(
  defineProps<{
    src?: string | null
    /** Teks alternatif + judul yang ditampilkan di bilah atas. */
    nama?: string
  }>(),
  { src: null, nama: '' }
)

const buka = defineModel<boolean>('open', { default: false })

const dialog = ref<HTMLDialogElement | null>(null)

const skala = ref(1)
const putar = ref(0) // derajat: 0 | 90 | 180 | 270
const x = ref(0)
const y = ref(0)

const SKALA_MIN = 1
const SKALA_MAKS = 5

// Pointer aktif untuk cubit/geser. Peta id→titik supaya multi-sentuh benar.
const pointer = new Map<number, { x: number; y: number }>()
let jarakAwal = 0
let skalaAwal = 1
let panAwal = { x: 0, y: 0 }
let ketukTerakhir = 0

function reset() {
  skala.value = 1
  putar.value = 0
  x.value = 0
  y.value = 0
  // Bersihkan seluruh jejak pointer sebelum sesi baru dimulai. Tanpa ini, SATU
  // pointer yang bocor — jari terangkat di luar panggung, atau dialog tertutup
  // saat jari masih menempel, sehingga `pointerup`/`pointercancel` tak pernah
  // sampai — membuat `pointer.size` salah SELAMANYA. Akibatnya geseran 1-jari
  // dikira cubit (size==2) → CUBIT-ZOOM & GESER MOGOK di setiap pratinjau
  // berikutnya. reset() dipanggil tiap kali lightbox dibuka, jadi keadaan
  // pointer selalu pulih.
  bersihkanPointer()
}

/** Kosongkan seluruh keadaan pointer/cubit agar tak ada yang basi antar sesi. */
function bersihkanPointer() {
  pointer.clear()
  titikTurun.clear()
  jarakAwal = 0
  skalaAwal = 1
  panAwal = { x: 0, y: 0 }
  ketukTerakhir = 0
}

/** Batasi nilai skala ke rentang yang sah. */
function jepitSkala(v: number) {
  return Math.min(SKALA_MAKS, Math.max(SKALA_MIN, v))
}

watch(buka, (nyala) => {
  const d = dialog.value
  if (!d) return
  if (nyala) {
    reset()
    if (!d.open) d.showModal()
  } else {
    // Saat ditutup, buang juga jejak pointer — kalau ada jari yang masih
    // "menempel" secara logis saat dialog tertutup, jangan sampai terbawa ke
    // pembukaan berikutnya.
    bersihkanPointer()
    if (d.open) d.close()
  }
})

// ESC / metode close bawaan → sinkronkan model.
function saatClose() {
  if (buka.value) buka.value = false
}

// Klik pada latar (bukan isi) menutup. Pada <dialog>, klik ::backdrop
// menjadikan dialog sendiri sebagai target.
function saatKlikDialog(e: MouseEvent) {
  if (e.target === dialog.value) buka.value = false
}

function tutup() {
  buka.value = false
}

function zoom(delta: number) {
  skala.value = jepitSkala(skala.value + delta)
  if (skala.value === 1) {
    x.value = 0
    y.value = 0
  }
}

function putarKanan() {
  putar.value = (putar.value + 90) % 360
  // Pan tak lagi bermakna setelah orientasi berubah; kembalikan ke tengah.
  x.value = 0
  y.value = 0
}

function saatRoda(e: WheelEvent) {
  e.preventDefault()
  zoom(e.deltaY < 0 ? 0.3 : -0.3)
}

function jarak2(a: { x: number; y: number }, b: { x: number; y: number }) {
  return Math.hypot(a.x - b.x, a.y - b.y)
}

function pointerDown(e: PointerEvent) {
  // `?.` HANYA menjaga bila metodenya tak ada — ia TIDAK menangkap lemparan.
  // setPointerCapture bisa melempar (mis. pointer sudah tak aktif). Bila itu
  // terjadi tanpa dijaga, baris di bawah (pointer.set) tak pernah jalan → geser
  // & cubit-zoom diam-diam mogok. Jadi bungkus, lalu LANJUTKAN melacak pointer.
  try {
    ;(e.target as HTMLElement).setPointerCapture?.(e.pointerId)
  } catch {
    /* abaikan: pelacakan pointer di bawah tetap harus berjalan */
  }
  pointer.set(e.pointerId, { x: e.clientX, y: e.clientY })

  if (pointer.size === 2) {
    const [a, b] = [...pointer.values()]
    jarakAwal = jarak2(a!, b!)
    skalaAwal = skala.value
  } else if (pointer.size === 1) {
    panAwal = { x: x.value, y: y.value }
    // Deteksi ketuk-ganda: dua ketukan cepat men-toggle zoom.
    const kini = Date.now()
    if (kini - ketukTerakhir < 300) {
      skala.value = skala.value > 1 ? 1 : 2.5
      if (skala.value === 1) {
        x.value = 0
        y.value = 0
      }
      ketukTerakhir = 0
    } else {
      ketukTerakhir = kini
    }
  }
}

function pointerMove(e: PointerEvent) {
  if (!pointer.has(e.pointerId)) return
  pointer.set(e.pointerId, { x: e.clientX, y: e.clientY })

  if (pointer.size === 2) {
    const [a, b] = [...pointer.values()]
    const jarakKini = jarak2(a!, b!)
    if (jarakAwal > 0) {
      skala.value = jepitSkala((skalaAwal * jarakKini) / jarakAwal)
    }
  } else if (pointer.size === 1 && skala.value > 1) {
    const p = [...pointer.values()][0]!
    const awal = [...pointer.keys()][0]
    // Geser mengikuti selisih posisi pointer dari titik turun.
    const dx = p.x - (titikTurun.get(awal!)?.x ?? p.x)
    const dy = p.y - (titikTurun.get(awal!)?.y ?? p.y)
    x.value = panAwal.x + dx
    y.value = panAwal.y + dy
  }
}

// Simpan posisi pointer saat pertama turun untuk menghitung delta pan.
const titikTurun = new Map<number, { x: number; y: number }>()
function pointerDownRekam(e: PointerEvent) {
  titikTurun.set(e.pointerId, { x: e.clientX, y: e.clientY })
  pointerDown(e)
}

function pointerUp(e: PointerEvent) {
  pointer.delete(e.pointerId)
  titikTurun.delete(e.pointerId)
  if (pointer.size < 2) jarakAwal = 0
}

const gayaGambar = computed(() => ({
  transform: `translate(${x.value}px, ${y.value}px) scale(${skala.value}) rotate(${putar.value}deg)`
}))

onBeforeUnmount(() => {
  if (dialog.value?.open) dialog.value.close()
})
</script>

<template>
  <Teleport to="body">
    <dialog
      ref="dialog"
      class="ca-lb"
      :aria-label="nama || 'Pratinjau gambar'"
      @close="saatClose"
      @click="saatKlikDialog"
      @cancel.prevent="tutup"
    >
      <div class="ca-lb-panel" @click.stop>
        <!-- Bilah atas: judul + tutup -->
        <div class="ca-lb-bar">
          <p class="ca-lb-judul">{{ nama || 'Pratinjau' }}</p>
          <button type="button" class="ca-lb-tombol" aria-label="Tutup pratinjau" @click="tutup">
            <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" fill="none" />
            </svg>
          </button>
        </div>

        <!-- Panggung gambar -->
        <div
          class="ca-lb-panggung"
          @wheel="saatRoda"
          @pointerdown="pointerDownRekam"
          @pointermove="pointerMove"
          @pointerup="pointerUp"
          @pointercancel="pointerUp"
        >
          <img
            v-if="src"
            :src="src"
            :alt="nama || 'Pratinjau gambar'"
            class="ca-lb-img"
            :style="gayaGambar"
            draggable="false"
          >
        </div>

        <!-- Kontrol bawah -->
        <div class="ca-lb-kontrol">
          <button type="button" class="ca-lb-tombol" aria-label="Perkecil" @click="zoom(-0.3)">
            <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
              <path d="M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round" fill="none" />
            </svg>
          </button>
          <span class="ca-lb-persen" aria-live="polite">{{ Math.round(skala * 100) }}%</span>
          <button type="button" class="ca-lb-tombol" aria-label="Perbesar" @click="zoom(0.3)">
            <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
              <path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round" fill="none" />
            </svg>
          </button>
          <button type="button" class="ca-lb-tombol" aria-label="Putar 90 derajat" @click="putarKanan">
            <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
              <path d="M4 12a8 8 0 108-8V1L7 5l5 4V6a6 6 0 11-6 6z" fill="currentColor" />
            </svg>
          </button>
          <button type="button" class="ca-lb-tombol" aria-label="Kembalikan tampilan awal" @click="reset">
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
              <path d="M3 12a9 9 0 019-9 9 9 0 016.7 3M21 12a9 9 0 01-9 9 9 9 0 01-6.7-3M17 4v4h-4M7 20v-4h4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" fill="none" />
            </svg>
          </button>
        </div>
      </div>
    </dialog>
  </Teleport>
</template>

<style scoped>
.ca-lb {
  margin: 0;
  padding: 0;
  border: 0;
  max-width: 100vw;
  max-height: 100dvh;
  width: 100vw;
  height: 100dvh;
  background: transparent;
  color: #fff;
  overflow: hidden;
}
.ca-lb::backdrop {
  background: rgba(5, 8, 20, 0.92);
}
.ca-lb-panel {
  position: fixed;
  inset: 0;
  display: flex;
  flex-direction: column;
}
.ca-lb-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: max(0.75rem, env(safe-area-inset-top)) 1rem 0.75rem;
}
.ca-lb-judul {
  font-size: 0.9rem;
  font-weight: 600;
  color: #fff;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ca-lb-panggung {
  flex: 1;
  display: grid;
  place-items: center;
  overflow: hidden;
  touch-action: none;
  min-height: 0;
}
.ca-lb-img {
  max-width: 96vw;
  max-height: 74dvh;
  object-fit: contain;
  user-select: none;
  -webkit-user-drag: none;
  will-change: transform;
  transition: transform 160ms ease;
  transform-origin: center;
}
/* Selama jari/tetikus menyeret, transisi dimatikan agar mengikuti tanpa jeda. */
.ca-lb-panggung:active .ca-lb-img {
  transition: none;
}
.ca-lb-kontrol {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem max(1rem, env(safe-area-inset-bottom));
}
.ca-lb-persen {
  min-width: 3.2rem;
  text-align: center;
  font-size: 0.85rem;
  font-variant-numeric: tabular-nums;
  color: rgba(255, 255, 255, 0.85);
}
.ca-lb-tombol {
  display: grid;
  place-items: center;
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 9999px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  cursor: pointer;
  transition: background 140ms ease, transform 140ms ease;
}
.ca-lb-tombol:hover {
  background: rgba(255, 255, 255, 0.16);
}
.ca-lb-tombol:active {
  transform: scale(0.94);
}
@media (prefers-reduced-motion: reduce) {
  .ca-lb-img,
  .ca-lb-tombol {
    transition: none !important;
  }
}
</style>
